import { useEffect, useMemo, useState } from 'react';
import helpIndex from '../help/index.json';

// Mirrors admin-portal/src/hooks/useHelpContent.ts (per-portal convention).
type HelpEntry = { file: string; title: string };
type HelpIndex = {
  routes: Record<string, HelpEntry>;
  fallback: HelpEntry;
};

const index = helpIndex as HelpIndex;

const helpFiles = import.meta.glob('../help/**/*.md', {
  query: '?raw',
  import: 'default',
}) as Record<string, () => Promise<string>>;

function patternToRegex(pattern: string): RegExp {
  const escaped = pattern.replace(/[.+*?^${}()|[\]\\]/g, '\\$&');
  const withParams = escaped.replace(/:[a-zA-Z_][a-zA-Z0-9_]*/g, '[^/]+');
  return new RegExp(`^${withParams}$`);
}

const compiledRoutes = Object.entries(index.routes)
  .map(([pattern, entry]) => ({
    pattern,
    regex: patternToRegex(pattern),
    entry,
    specificity: (pattern.match(/:/g) || []).length === 0 ? 1 : 0,
  }))
  .sort((a, b) => b.specificity - a.specificity);

function matchRoute(pathname: string): HelpEntry {
  for (const { regex, entry } of compiledRoutes) {
    if (regex.test(pathname)) return entry;
  }
  return index.fallback;
}

function fileKey(relativeFile: string): string {
  return `../help/${relativeFile}`;
}

export interface HelpContentState {
  title: string;
  content: string | null;
  loading: boolean;
  error: string | null;
}

export function useHelpContent(pathname: string): HelpContentState {
  const entry = useMemo(() => matchRoute(pathname), [pathname]);
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const key = fileKey(entry.file);
    const loader = helpFiles[key];

    if (!loader) {
      setContent(null);
      setError(`Help content not found: ${entry.file}`);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    loader()
      .then((md) => {
        if (cancelled) return;
        setContent(md);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load help');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [entry.file]);

  return { title: entry.title, content, loading, error };
}
